<script setup lang="ts">
/**
 * A short video of one feature on the page that describes it, from the
 * series in doc/video/features (the 16:9 cut; the 9:16 reels are in the
 * affiliate materials). There is a cut in English, Czech and German, the
 * whole of it written for that language, so each page plays its own.
 *
 * `preload="none"` keeps the 3–4 MB file off the first load: only the poster
 * is fetched until someone presses play.
 */
import { FEATURE_VIDEOS, type FeatureVideoId, featureVideoLang, featureVideoPath } from '~/constants/featureVideos'

const props = withDefaults(defineProps<{
  video: FeatureVideoId
  /** The band's background, to keep the page's alternation. */
  surface?: 'white' | 'alt'
}>(), { surface: 'white' })

const { t, locale } = useI18n()
const lang = computed(() => featureVideoLang(locale.value))
const src = computed(() => featureVideoPath(props.video, lang.value))
const poster = computed(() => featureVideoPath(props.video, lang.value, 'jpg'))
const seconds = computed(() => FEATURE_VIDEOS[props.video].seconds[lang.value])
</script>

<template>
  <section class="sofax overflow-hidden" :class="surface === 'alt' ? 'surface-alt' : 'bg-white dark:bg-gray-950'">
    <div class="section-space">
      <div class="shell">
        <SofaxSectionHeading
          :eyebrow="t('featureVideo.eyebrow', { seconds })"
          :title="t(`featureVideo.items.${video}.title`)"
          :text="t(`featureVideo.items.${video}.text`)"
          width="max-w-4xl"
        />

        <div class="mx-auto max-w-5xl overflow-hidden rounded-[0.625rem] border border-line bg-gray-900 shadow-lift dark:border-gray-700">
          <!-- :key swaps the element when the language changes, so the new source loads -->
          <video
            :key="src"
            class="aspect-video w-full"
            :src="src"
            :poster="poster"
            :aria-label="t(`featureVideo.items.${video}.title`)"
            controls
            muted
            playsinline
            preload="none"
            width="1280"
            height="720"
          />
        </div>
      </div>
    </div>
  </section>
</template>
